/**
 * LeafConvertAdd — the "+ or" / "+ and" chip on a standalone top-level
 * WHERE / HAVING condition in the Inline pill view. Picking a column groups
 * THIS condition with a new one, joined by the OPPOSITE of the connector of
 * the list it sits in — the only grouping that changes the result:
 * `a AND b` → `(a OR x) AND b`; in an OR list `(a AND x) OR b`. Grouping with
 * the same connector would only add parentheses, so the picker has no AND/OR
 * step. Commits through `groupLeaf(path, …)`.
 *
 * Distinct from the footer "+ Add condition" (which appends a sibling at the
 * clause root, NOT a group) and from a group box's end chip (which adds INTO an
 * existing group, with that group's connector). Placement — a hover / focus
 * badge — is `ConditionChipSlot`'s.
 *
 * HAVING parity (Plan 3 Task 5): `kind` (required — the caller always knows
 * it) parameterizes the testid, the `feature:vqe_${kind}_node_edit` analytics,
 * threads `clause` into `groupLeaf` so a HAVING leaf converts into a HAVING
 * group (not just cosmetic — without `clause` this would silently target the
 * WHERE tree), and drives the chip's field-step content (`'having'` leads
 * with the Aggregates templates).
 */

import type { ReactElement } from 'react';

import { useAnalytics } from '../../../../../core/analytics/useAnalytics';
import type { BooleanClauseTarget, NodePath } from '../../../../sql-resolver/types/structuralEdit';
import type { VqeEditContext } from '../../edit/editContext';
import { toCommitCtx } from '../../edit/editContext';
import { groupLeaf } from '../../edit/edits/groupLeaf';
import { vqeCopy } from '../../ux/vqeCopy';
import { VqeFilterChipBuilder } from '../common/VqeFilterChipBuilder';
import { ConditionChipSlot } from './ConditionChipSlot';
import { oppositeConnector, type WhereConnector } from './whereFragments';

export function LeafConvertAdd({
  path,
  editContext,
  kind,
  listConnector,
}: {
  readonly path: NodePath;
  readonly editContext: VqeEditContext;
  readonly kind: BooleanClauseTarget;
  /** Connector of the list this condition sits in (`parentConnectorOf`). */
  readonly listConnector: WhereConnector;
}): ReactElement {
  const pathKey = path.join('-') || 'root';
  const { track } = useAnalytics();
  const connector = oppositeConnector(listConnector);
  const or = connector === 'OR';

  return (
    <ConditionChipSlot>
      {(onDraftOpenChange) => (
        <VqeFilterChipBuilder
          mode="add"
          testId={`vqe-${kind}-leaf-${pathKey}-group-add`}
          triggerLabel={or ? vqeCopy.conditionChip.or : vqeCopy.conditionChip.and}
          triggerVariant="chip"
          hint={or ? vqeCopy.hint.groupWithOr : vqeCopy.hint.groupWithAnd}
          editContext={editContext}
          combinator={connector}
          clause={kind}
          onDraftOpenChange={onDraftOpenChange}
          onCommit={(text) => {
            void groupLeaf(path, text, connector, toCommitCtx(editContext), kind);
            track(`feature:vqe_${kind}_node_edit`, { action: 'group', connector: or ? 'or' : 'and' });
          }}
        />
      )}
    </ConditionChipSlot>
  );
}
